import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useTranslation } from "react-i18next";

const getIdsFromCollection = (collection) => {
  if (Array.isArray(collection)) return collection.filter(Boolean);
  if (collection && typeof collection === "object") return Object.keys(collection);
  return [];
};

function BookingSelector({
  featureKey,
  resourceCollectionKey,
  resourceLabel,
  projects,
  resources,
  isProjectManager,
  userProjects,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation();

  const [projectId, setProjectId] = useState("");
  const [resourceId, setResourceId] = useState("");

  const projectOptions = useMemo(() => {
    const allProjects = Object.entries(projects || {}).map(([uid, project]) => ({
      uid,
      ...project,
    }));

    return allProjects
      .filter((project) => {
        const hasFeatureEnabled = project?.features?.[featureKey] === true;

        if (!hasFeatureEnabled) return false;
        if (!isProjectManager) return true;

        return !!userProjects?.[project?.uid];
      })
      .sort((projectA, projectB) =>
        `${projectA?.name || ""}`.localeCompare(`${projectB?.name || ""}`, undefined, {
          sensitivity: "base",
          numeric: true,
        })
      );
  }, [projects, featureKey, isProjectManager, userProjects]);

  const resourceOptions = useMemo(() => {
    const selectedProject = projectId ? projects?.[projectId] : null;
    const resourceIds = getIdsFromCollection(selectedProject?.[resourceCollectionKey]);

    return resourceIds
      .map((uid) => ({ uid, ...(resources?.[uid] || {}) }))
      .filter((resource) => !!resource?.uid)
      .sort((resourceA, resourceB) =>
        `${resourceA?.name || ""}`.localeCompare(`${resourceB?.name || ""}`, undefined, {
          sensitivity: "base",
          numeric: true,
        })
      );
  }, [projectId, projects, resourceCollectionKey, resources]);

  useEffect(() => {
    if (!projectId || projectOptions.some((project) => project?.uid === projectId)) return;

    setProjectId("");
    setResourceId("");
  }, [projectId, projectOptions]);

  useEffect(() => {
    if (!resourceId || resourceOptions.some((resource) => resource?.uid === resourceId)) return;

    setResourceId("");
  }, [resourceId, resourceOptions]);

  useEffect(() => {
    if (projectId || projectOptions.length !== 1) return;

    setProjectId(projectOptions[0].uid);
  }, [projectId, projectOptions]);

  const hasProjectOptions = projectOptions.length > 0;
  const hasResourceOptions = resourceOptions.length > 0;

  return (
    <Card id="basic-info" sx={{ overflow: "visible" }}>
      <MDBox
        height="4rem"
        bgColor="info"
        variant="gradient"
        coloredShadow="info"
        borderRadius="xl"
        display="flex"
        justifyContent="center"
        alignItems="center"
        color="white"
        mt={-3}
        mx={10}
        px={2}
      >
        {featureKey === "amenities" ? t("emailBookingAmenity") : t("emailBookingService")}
      </MDBox>

      <MDBox pt={3} pb={3} px={3}>
        <MDTypography variant="button" color="text" display="block" mb={2}>
          {t("selectProject")}
        </MDTypography>

        <FormControl fullWidth variant="standard" sx={{ mb: 3 }}>
          <InputLabel>{t("project")}</InputLabel>
          <Select
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
              setResourceId("");
            }}
            label={t("project")}
          >
            {projectOptions.map((project) => (
              <MenuItem key={project.uid} value={project.uid}>
                {project?.name || project.uid}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {projectId && (
          <FormControl fullWidth variant="standard" sx={{ mb: 3 }}>
            <InputLabel>{t(resourceLabel)}</InputLabel>
            <Select
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
              label={t(resourceLabel)}
            >
              {resourceOptions.map((resource) => (
                <MenuItem key={resource.uid} value={resource.uid}>
                  {resource?.name || resource.uid}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!hasProjectOptions && (
          <MDTypography variant="caption" color="error" display="block" mb={2}>
            {t("noProjectsAvailable")}
          </MDTypography>
        )}

        {projectId && !hasResourceOptions && (
          <MDTypography variant="caption" color="error" display="block" mb={2}>
            {t("noResourcesAvailable")}
          </MDTypography>
        )}

        <MDBox display="flex" justifyContent="flex-end" gap={1}>
          <MDButton variant="outlined" color="secondary" onClick={onClose}>
            {t("btnClose")}
          </MDButton>

          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => onSubmit({ projectId, resourceId })}
            disabled={!projectId || !resourceId}
          >
            {t("btnOpen")}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

BookingSelector.defaultProps = {
  featureKey: "amenities",
  resourceCollectionKey: "amenities",
  resourceLabel: "amenity",
  projects: {},
  resources: {},
  isProjectManager: false,
  userProjects: {},
};

BookingSelector.propTypes = {
  featureKey: PropTypes.string,
  resourceCollectionKey: PropTypes.string,
  resourceLabel: PropTypes.string,
  projects: PropTypes.object,
  resources: PropTypes.object,
  isProjectManager: PropTypes.bool,
  userProjects: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default BookingSelector;
